import { useEffect } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import Header from '../../components/layout/Header';
import Footer from '../../components/layout/Footer';
import { ArticleContent } from '../../components/insights/ArticleContent';
import { InsightsSidebar } from '../../components/insights/InsightsSidebar';
import { ShareBar } from '../../components/insights/ShareBar';
import { TOPIC_ICONS } from '../../components/insights/topicIcons';
import { INSIGHTS_TOPICS, getTopic, readingTime } from '../../data/insights';
import { articleSeo } from '../../seo/insightsSeo';
import { useSeo } from '../../hooks/useSeo';
import { ArrowLeft, ArrowRight, Clock, ChevronRight, BookOpen, FileText, Shield, Sparkles, CalendarDays } from 'lucide-react';

const NavCard = ({ item, direction }) => {
  const isPrev = direction === 'prev';
  return (
    <Link
      to={item.href}
      className={`flex-1 card-ink card-ink--hover p-5 ${isPrev ? '' : 'text-right'}`}
      data-testid={`insights-${direction}-link`}
    >
      <span className={`flex items-center gap-1.5 label-caps text-ink/60 mb-2 ${isPrev ? '' : 'justify-end'}`}>
        {isPrev && <ArrowLeft className="w-4 h-4" />}
        {isPrev ? 'Vorheriger Artikel' : 'Nächster Artikel'}
        {!isPrev && <ArrowRight className="w-4 h-4" />}
      </span>
      <span className="font-display font-bold text-lg leading-snug">{item.title}</span>
    </Link>
  );
};

const Takeaways = ({ items, variant }) => {
  if (!items?.length) return null;
  if (variant === 1) {
    return (
      <aside className="mb-10 card-ink bg-purple text-off p-6 md:p-8" data-testid="article-takeaways">
        <p className="flex items-center gap-2 label-caps text-lilac mb-4"><Sparkles className="w-4 h-4" />Das Wichtigste in Kürze</p>
        <ol className="space-y-3">
          {items.map((t, i) => (
            <li key={i} className="flex gap-4 text-lg leading-relaxed"><span className="font-display font-extrabold text-lilac tabular-nums">{i + 1}.</span>{t}</li>
          ))}
        </ol>
      </aside>
    );
  }
  if (variant === 2) {
    return (
      <aside className="mb-10 grid sm:grid-cols-2 gap-4" data-testid="article-takeaways">
        {items.map((t, i) => (
          <div key={i} className="card-ink card-ink--off p-5 text-[1.05rem] leading-relaxed">
            <span className="block label-caps text-purple mb-2">Kernaussage {i + 1}</span>
            {t}
          </div>
        ))}
      </aside>
    );
  }
  return (
    <aside className="mb-10 card-ink card-ink--shadow-sm p-6 md:p-7" data-testid="article-takeaways">
      <p className="flex items-center gap-2 font-display font-bold text-xl mb-4"><Sparkles className="w-5 h-5 text-purple" />Das Wichtigste in Kürze</p>
      <ul className="grid md:grid-cols-2 gap-x-8 gap-y-3">
        {items.map((t, i) => (
          <li key={i} className="flex gap-3 text-[1.05rem] leading-relaxed"><span className="star flex-shrink-0" aria-hidden="true">✳</span>{t}</li>
        ))}
      </ul>
    </aside>
  );
};

const MidCta = ({ variant, topicTitle }) => (
  <aside
    className={`my-12 card-ink p-6 md:p-8 flex flex-col md:flex-row md:items-center gap-5 ${variant === 1 ? 'bg-deep text-off' : 'card-ink--lilac'}`}
    data-testid="article-mid-cta"
  >
    <div className="flex-1">
      <p className="font-display font-bold text-xl mb-1">Du willst {topicTitle} in deinem Betrieb angehen?</p>
      <p className={`text-[1.05rem] ${variant === 1 ? 'opacity-90' : 'text-ink/85'}`}>Wir schauen in 30 Minuten gemeinsam auf deine Situation – kostenfrei und herstellerneutral.</p>
    </div>
    <Link to="/erstberatung" className={`btn flex-shrink-0 ${variant === 1 ? 'btn--lilac' : 'btn--purple'}`}>
      Erstberatung anfragen
      <ArrowRight className="w-5 h-5" />
    </Link>
  </aside>
);

const AuthorBox = ({ topic }) => (
  <footer className="mt-12 card-ink card-ink--off p-6 flex flex-col sm:flex-row gap-5 items-start" data-testid="article-author-box">
    <img src="/icons/icon-192.png" alt="gastro-beratung.de" width="56" height="56" className="w-14 h-14 border-2 border-ink bg-white flex-shrink-0" loading="lazy" />
    <div className="text-[0.95rem] text-ink/85 leading-relaxed">
      <p className="font-display font-bold text-ink text-lg mb-1">Geschrieben vom Team von gastro-beratung.de</p>
      <p>
        Unsere Insights entstehen aus Beratungsprojekten mit Gastronomiebetrieben aller Größen – von der Espressobar bis zur Systemgastronomie. Wir sind unabhängig, verkaufen keine Software und nennen deshalb keine Anbieter.{' '}
        <Link to="/ueber-uns" className="text-purple font-bold underline underline-offset-4 hover:text-deep">Mehr über uns</Link>
      </p>
      <p className="mt-3 inline-flex items-center gap-1.5 text-sm text-ink/60">
        <CalendarDays className="w-4 h-4" />
        Zuletzt aktualisiert: <time dateTime={topic.dateModified}>{topic.updated}</time>
      </p>
    </div>
  </footer>
);

const TopicChip = ({ t }) => {
  const TIcon = TOPIC_ICONS[t.icon];
  return (
    <Link to={`/insights/${t.slug}`} className="badge-ink badge-ink--white normal-case tracking-normal text-[0.95rem] font-medium hover:bg-lilac transition-colors">
      {TIcon && <TIcon className="w-4 h-4 text-purple" />}
      {t.shortTitle}
    </Link>
  );
};

const InsightsArticlePage = () => {
  const { topicSlug, articleSlug } = useParams();
  const topic = getTopic(topicSlug);
  const isMain = !articleSlug;
  const article = topic ? (isMain ? topic.mainArticle : topic.subArticles.find((a) => a.slug === articleSlug)) : null;
  const seo = topic && article ? articleSeo(topic, article, isMain) : { title: 'Insights | gastro-beratung.de', description: '', path: '/insights', noindex: true };
  useSeo(seo);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [topicSlug, articleSlug]);

  if (!topic) return <Navigate to="/insights" replace />;
  if (!article) return <Navigate to={`/insights/${topic.slug}`} replace />;

  const topicIndex = INSIGHTS_TOPICS.indexOf(topic);
  const variant = topicIndex % 3;
  const imageLeft = topicIndex % 2 === 1;
  const Icon = TOPIC_ICONS[topic.icon];
  const subIndex = isMain ? -1 : topic.subArticles.findIndex((a) => a.slug === articleSlug);
  const toNav = (a, i) => (i < 0 ? { title: `Leitfaden: ${topic.mainArticle.title}`, href: `/insights/${topic.slug}` } : { title: a.title, href: `/insights/${topic.slug}/${a.slug}` });
  const prev = isMain ? null : subIndex === 0 ? toNav(null, -1) : toNav(topic.subArticles[subIndex - 1], subIndex - 1);
  const next = isMain ? (topic.subArticles[0] ? toNav(topic.subArticles[0], 0) : null) : topic.subArticles[subIndex + 1] ? toNav(topic.subArticles[subIndex + 1], subIndex + 1) : null;
  const otherTopics = INSIGHTS_TOPICS.filter((t) => t.slug !== topic.slug);
  const shareUrl = `${process.env.REACT_APP_BACKEND_URL}/api/insights/share/${topic.slug}${isMain ? '' : `/${article.slug}`}`;
  const midCtaAfter = isMain && article.sections.length > 5 ? 2 : -1;

  const meta = (
    <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-[0.95rem] text-ink/70">
      <span className="inline-flex items-center gap-1.5"><Clock className="w-4 h-4" />{readingTime(article)} Min. Lesezeit</span>
      <span className="inline-flex items-center gap-1.5"><CalendarDays className="w-4 h-4" />Aktualisiert: <time dateTime={topic.dateModified}>{topic.updated}</time></span>
      <span className="inline-flex items-center gap-1.5"><Shield className="w-4 h-4" />Herstellerneutral</span>
    </div>
  );

  const kindBadge = (
    <span className="badge-ink badge-ink--purple" data-testid="article-kind-badge">
      {isMain ? <BookOpen className="w-4 h-4" /> : <FileText className="w-4 h-4" />}
      {isMain ? 'Leitfaden' : 'Vertiefung'}
    </span>
  );

  return (
    <>
      <Header />
      <main data-testid="insights-article-page" data-variant={variant}>
        <article>
          <header className={`border-b-2 border-ink ${isMain ? 'bg-off' : 'bg-lilac/40'}`}>
            <div className="wrap pt-6 pb-12 md:pb-16">
              <nav aria-label="Breadcrumb" className="flex flex-wrap items-center gap-1.5 text-[0.95rem] text-ink/70 mb-8" data-testid="insights-breadcrumb">
                <Link to="/insights" className="hover:text-purple transition-colors">Insights</Link>
                <ChevronRight className="w-4 h-4" />
                {isMain ? (
                  <span className="text-ink font-medium">{topic.title}</span>
                ) : (
                  <>
                    <Link to={`/insights/${topic.slug}`} className="hover:text-purple transition-colors">{topic.title}</Link>
                    <ChevronRight className="w-4 h-4" />
                    <span className="text-ink font-medium">{article.shortTitle || article.title}</span>
                  </>
                )}
              </nav>

              {isMain ? (
                <div className={`grid lg:grid-cols-12 gap-10 items-center ${imageLeft ? 'lg:[&>*:first-child]:order-2' : ''}`}>
                  <div className="lg:col-span-7">
                    <div className="flex items-center gap-3 mb-6">
                      {Icon && (
                        <span className="w-11 h-11 border-2 border-ink bg-white flex items-center justify-center">
                          <Icon className="w-6 h-6 text-purple" />
                        </span>
                      )}
                      {kindBadge}
                    </div>
                    <h1 className="display-2 mb-5" data-testid="article-title">{article.title}</h1>
                    <p className="text-lg md:text-xl text-ink/85 mb-6 max-w-[60ch]" data-testid="article-intro">{article.intro}</p>
                    {meta}
                  </div>
                  <figure className="hidden lg:block lg:col-span-5 m-0">
                    <div className={`polaroid ${variant === 2 ? 'polaroid--r2' : 'polaroid--r1'} max-w-[460px] ${imageLeft ? 'mr-auto' : 'ml-auto'}`}>
                      <div className="polaroid__media aspect-[4/3]">
                        <img src={topic.image} alt={`${topic.title} – Symbolbild`} width="600" height="450" className="absolute inset-0 w-full h-full object-cover" fetchPriority="high" />
                      </div>
                      <figcaption>
                        <span className="polaroid__name block">{topic.title}</span>
                        <div className="polaroid__cat">{topic.subArticles.length} Vertiefungen</div>
                      </figcaption>
                    </div>
                  </figure>
                </div>
              ) : (
                <div className="max-w-3xl">
                  <div className="flex flex-wrap items-center gap-3 mb-6">
                    {kindBadge}
                    <span className="text-[0.95rem] text-ink/70" data-testid="article-series-position">
                      Teil {subIndex + 1} von {topic.subArticles.length} · <Link to={`/insights/${topic.slug}`} className="text-purple underline underline-offset-4 hover:text-deep">{topic.title}</Link>
                    </span>
                  </div>
                  <h1 className="display-2 mb-5" data-testid="article-title">{article.title}</h1>
                  <div className={`h-1.5 w-24 mb-6 border border-ink ${variant === 1 ? 'bg-purple' : 'bg-lilac'}`} />
                  <p className="text-lg md:text-xl text-ink/85 mb-6" data-testid="article-intro">{article.intro}</p>
                  {meta}
                </div>
              )}
            </div>
          </header>

          <section className="py-12 md:py-16 bg-off">
            <div className="wrap">
              <div className="grid lg:grid-cols-[minmax(0,1fr)_320px] gap-12">
                <div className="min-w-0">
                  {isMain && <Takeaways items={article.takeaways} variant={variant} />}

                  <ArticleContent
                    sections={article.sections}
                    variant={variant}
                    numbered={isMain}
                    dropCap={!isMain && variant !== 0}
                    insertAfter={midCtaAfter}
                    insertNode={<MidCta variant={variant} topicTitle={topic.shortTitle} />}
                  />

                  <div className="mt-6 border-2 border-ink bg-white p-5 text-[0.95rem] text-ink/85 flex items-start gap-3" data-testid="article-neutrality-note">
                    <Shield className="w-5 h-5 text-purple flex-shrink-0 mt-0.5" />
                    <span>Herstellerneutral: In unseren Insights nennen wir bewusst keine konkreten Systeme oder Anbieter. Welche Lösung zu deinem Betrieb passt, klären wir gemeinsam in der Beratung.</span>
                  </div>

                  <div className="mt-8">
                    <ShareBar shareUrl={shareUrl} title={article.title} />
                  </div>

                  <AuthorBox topic={topic} />

                  {isMain && topic.subArticles.length > 0 && (
                    <section className="mt-14" data-testid="sub-articles-section">
                      <h2 className="display-3 mb-6">Vertiefende Artikel zu {topic.title}</h2>
                      <div className="grid sm:grid-cols-2 gap-5">
                        {topic.subArticles.map((a, i) => (
                          <Link key={a.slug} to={`/insights/${topic.slug}/${a.slug}`} className="group card-ink card-ink--hover p-6 flex flex-col" data-testid={`sub-article-card-${a.slug}`}>
                            <span className="flex items-center justify-between label-caps text-ink/60 mb-3">
                              <span>Teil {i + 1}</span>
                              <span className="inline-flex items-center gap-1.5 normal-case tracking-normal font-sans font-normal"><Clock className="w-4 h-4" />{readingTime(a)} Min.</span>
                            </span>
                            <h3 className="font-display font-bold text-xl mb-2 leading-snug">{a.title}</h3>
                            <p className="text-[1rem] text-ink/85 leading-relaxed flex-1">{a.teaser}</p>
                            <span className="inline-flex items-center gap-2 mt-4 text-purple font-display font-bold group-hover:gap-3 transition-all">Artikel lesen <ArrowRight className="w-5 h-5" /></span>
                          </Link>
                        ))}
                      </div>
                    </section>
                  )}

                  {(prev || next) && (
                    <nav aria-label="Artikel-Navigation" className="mt-14 flex flex-col sm:flex-row gap-4" data-testid="article-pagination">
                      {prev ? <NavCard item={prev} direction="prev" /> : <div className="flex-1 hidden sm:block" />}
                      {next ? <NavCard item={next} direction="next" /> : <div className="flex-1 hidden sm:block" />}
                    </nav>
                  )}
                </div>

                <div>
                  <InsightsSidebar topic={topic} currentSlug={isMain ? null : articleSlug} sections={article.sections} />
                </div>
              </div>
            </div>
          </section>
        </article>

        <section className="sec--tight sec--lilac border-t-2 border-ink">
          <div className="wrap">
            <h2 className="display-3 mb-6">Weitere Themenbereiche</h2>
            <div className="flex flex-wrap gap-2.5" data-testid="other-topics">
              {otherTopics.map((t) => <TopicChip key={t.slug} t={t} />)}
            </div>
          </div>
        </section>

        <section className="sec sec--purple">
          <div className="wrap">
            <h2 className="display-2 max-w-[22ch] mb-4">Lass uns gemeinsam deine <span className="serif-it text-lilac">digitale</span> Landschaft strukturieren.</h2>
            <p className="text-lg opacity-90 max-w-[50ch] mb-10">Kostenfrei, unverbindlich und auf Augenhöhe – so starten wir mit allen neuen Partnern.</p>
            <Link to="/erstberatung" className="btn btn--lilac" data-testid="article-bottom-cta">
              Kostenlose Beratung starten
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
};

export default InsightsArticlePage;
